/**
 * 일지 입력표 공통 부품
 * ---------------------------------------------------------------------------
 * 세 서식이 같은 모양의 표를 여럿 쓴다. 셀 하나하나를 매번 쓰면 표마다
 * 잠금 처리와 null 변환이 조금씩 달라지고, 그 차이가 버그가 된다.
 *
 * antd Form 을 쓰지 않는 이유
 *   T03 한 건은 표가 9개, 셀이 수백 개다. Form.List 로 다루면 한 칸을 고칠
 *   때마다 전체가 다시 그려져 현장의 저사양 단말에서 눈에 띄게 느려진다.
 *   상태는 평범한 배열로 두고 셀만 제어 컴포넌트로 만든다.
 */

import { Checkbox, DatePicker, Input, InputNumber, Tag, TimePicker, Tooltip } from 'antd';
import dayjs from 'dayjs';
import type { HourFlags } from '@eco/shared';

/* =========================================================================
 * 셀
 * ====================================================================== */

export function TextCell({
  value,
  disabled,
  maxLength = 200,
  placeholder,
  onChange,
}: {
  value: string | null;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  onChange: (v: string | null) => void;
}) {
  return (
    <Input
      size="small"
      value={value ?? ''}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      // 빈 문자열은 null 로 바꿔 보낸다. 서버 스키마가 '' 를 null 로 다루지만
      // 화면 상태에서도 같은 모양이어야 비교와 복사가 어긋나지 않는다.
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    />
  );
}

export function NumberCell({
  value,
  disabled,
  min,
  max,
  step,
  placeholder,
  onChange,
}: {
  value: number | null;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  onChange: (v: number | null) => void;
}) {
  return (
    <InputNumber
      size="small"
      style={{ width: '100%' }}
      value={value ?? undefined}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      onChange={(v) => onChange(v === undefined || v === null ? null : Number(v))}
    />
  );
}

export function TimeCell({
  value,
  disabled,
  onChange,
}: {
  value: string | null;
  disabled?: boolean;
  onChange: (v: string | null) => void;
}) {
  return (
    <TimePicker
      size="small"
      format="HH:mm"
      minuteStep={10}
      style={{ width: '100%' }}
      disabled={disabled}
      value={value ? dayjs(value, 'HH:mm') : null}
      onChange={(v) => onChange(v ? v.format('HH:mm') : null)}
    />
  );
}

/**
 * 날짜 한 칸. 서식의 「분석일」처럼 년월일만 받는 칸에 쓴다.
 *
 * 저장은 시각까지 있는 값이라 그날 0시로 잡는다 — 분석일은 날짜만 뜻하므로
 * 시각을 사람에게 묻지 않는다.
 */
export function DateCell({
  value,
  disabled,
  onChange,
}: {
  value: string | null;
  disabled?: boolean;
  onChange: (v: string | null) => void;
}) {
  return (
    <DatePicker
      size="small"
      style={{ width: '100%' }}
      format="YYYY-MM-DD"
      disabled={disabled}
      value={value ? dayjs(value) : null}
      onChange={(v) => onChange(v ? v.startOf('day').toISOString() : null)}
    />
  );
}

/**
 * 기간 한 칸. 서식의 「보 수 기 간」 이 한 칸이고 년월일만 받는다.
 *
 * 저장은 시각까지 있는 값이라 시작은 그날 0시, 종료는 그날 끝으로 잡는다.
 * 끝을 0시로 두면 하루짜리 보수가 "길이 0" 이 되고, 시작·종료 순서를 보는
 * 검사에도 걸린다.
 */
export function DateRangeCell({
  start,
  end,
  disabled,
  onChange,
}: {
  start: string | null;
  end: string | null;
  disabled?: boolean;
  onChange: (start: string | null, end: string | null) => void;
}) {
  return (
    <DatePicker.RangePicker
      size="small"
      style={{ width: '100%' }}
      format="YYYY-MM-DD"
      disabled={disabled}
      allowEmpty={[true, true]}
      placeholder={['보수 시작', '보수 종료']}
      value={[start ? dayjs(start) : null, end ? dayjs(end) : null]}
      onChange={(v) =>
        onChange(
          v?.[0] ? v[0].startOf('day').toISOString() : null,
          v?.[1] ? v[1].endOf('day').toISOString() : null,
        )
      }
    />
  );
}

/* =========================================================================
 * 24시간 가동표
 * ====================================================================== */

/** 한 시간 칸의 최소 너비(px). 24칸을 다 펼치면 이 값의 24배가 된다. */
const HOUR_CELL = 34;

/**
 * 1시~24시 체크박스 24칸.
 *
 * 한 칸씩 누르는 것은 24번 클릭이다. 실무에서는 "08시부터 18시까지" 처럼
 * 연속 구간이 대부분이라 드래그로 칠할 수 있게 했다.
 *
 * 칸을 화면 폭에 맞춰 늘이지 않고 최소 너비를 두고 가로로 밀어 본다. 좁은
 * 화면에서 24칸을 억지로 욱여넣으면 체크박스가 서로 붙어 옆 시간을 누르게
 * 된다 — 시간이 한 칸 밀린 일지는 사람이 알아채기 어렵다.
 */
export function HourGrid({
  hours,
  disabled,
  onChange,
}: {
  hours: HourFlags;
  disabled?: boolean;
  onChange: (hours: HourFlags) => void;
}) {
  const total = hours.filter(Boolean).length;

  const setAt = (index: number, value: boolean) => {
    const next = [...hours];
    next[index] = value;
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {/* minWidth: 0 이 없으면 flex 항목이 내용 너비만큼 벌어져 스크롤이 생기지 않는다. */}
      <div style={{ flex: 1, minWidth: 0, overflowX: 'auto', paddingBottom: 2 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(24, minmax(${HOUR_CELL}px, 1fr))`,
            gap: 1,
          }}
        >
          {hours.map((on, i) => (
            <Tooltip key={i} title={`${i + 1}시`} mouseEnterDelay={0.4}>
              <label
                style={{
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 10,
                  lineHeight: '12px',
                  color: on ? '#0f7b6c' : '#bbb',
                  cursor: disabled ? 'default' : 'pointer',
                  userSelect: 'none',
                }}
                // 드래그로 연속 구간을 칠한다. 마우스 버튼이 눌린 채 지나가면 켠다.
                onMouseEnter={(e) => {
                  if (!disabled && e.buttons === 1) setAt(i, true);
                }}
              >
                <Checkbox
                  checked={on}
                  disabled={disabled}
                  onChange={(e) => setAt(i, e.target.checked)}
                />
                <span>{i + 1}</span>
              </label>
            </Tooltip>
          ))}
        </div>
      </div>
      {/* 가동 시간 합계. 서식에 적히는 값이므로 눈에 띄어야 한다. */}
      <Tag
        color={total > 0 ? 'green' : 'default'}
        style={{
          margin: 0,
          minWidth: 62,
          textAlign: 'center',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: '22px',
        }}
      >
        {total}시간
      </Tag>
    </div>
  );
}

/* =========================================================================
 * 행 배열 조작
 * ====================================================================== */

/** 배열의 한 행만 바꾼 새 배열을 돌려준다. */
export function patchRow<T>(rows: T[], index: number, patch: Partial<T>): T[] {
  return rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
}

export function removeRow<T>(rows: T[], index: number): T[] {
  return rows.filter((_, i) => i !== index).map((r, i) => ({ ...r, sortOrder: i }));
}

export function appendRow<T>(rows: T[], row: T): T[] {
  return [...rows, { ...row, sortOrder: rows.length }];
}
